import React, { useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Fade,
  FormControlLabel,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { styled } from '@mui/material/styles';
import { useOrgDepartmentsStore } from '@libs/store/settings/departments';
import { useLocationStore } from '@libs/store/locations';
import { useTalentsStore } from '@libs/store/talents';
import { ActiveFilters, FiltersDisplayCollection } from '@molecules/filters';
import { FilterMultiSelect, renderCheckboxOption } from '@atoms/inputs';
import { Button, MenuSplitButton } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { useCalendarFilters } from '@libs/store/shifts';
import { CalendarShiftsFilterMenu } from '../shiftsList/filterMenu';

type CalendarFilterBarProps = {
  loading?: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  weekStartDay: number;
  onToday: () => void;
  onWeekBack: () => void;
  onWeekForward: () => void;
  onNewShift: () => void;
  onNewSeries: () => void;
  onNewRotation: () => void;
  onListRefresh: () => void;
  onPublish: () => void;
  onShare: () => void;
  onClearFilters: () => void;
  onDateRangeChange?: (start: Dayjs, end: Dayjs) => void;
};

export function CalendarFilterBar({
  loading = false,
  startDate,
  endDate,
  weekStartDay,
  onToday,
  onWeekBack,
  onWeekForward,
  onNewShift,
  onNewSeries,
  onNewRotation,
  onListRefresh,
  onPublish,
  onShare,
  onClearFilters,
  onDateRangeChange,
}: CalendarFilterBarProps) {
  const theme = useTheme();
  const intl = useIntl();
  const { current: currentLocation } = useLocationStore();

  const { activeFilters, clearFilter, getFilter, setFilter, setFilterFromOptions, loadData, clearFilters } =
    useCalendarFilters();
  const { departments, loadTree, initiated, departmentsTreeLoading } = useOrgDepartmentsStore();
  const { load: loadTalents } = useTalentsStore();

  useEffect(() => {
    if (!initiated && !departmentsTreeLoading) {
      loadTree();
    }
  }, [loadTree, initiated, departmentsTreeLoading]);

  useEffect(() => {
    if (currentLocation) {
      loadData({
        locationId: currentLocation.id,
      });
    }
  }, [loadData, currentLocation]);

  const statusFilterOptions = departments.map((department) => ({
    label: department.label,
    value: department.id,
  }));

  const filtersDisplayCollection: FiltersDisplayCollection = Object.keys(
    activeFilters,
  ).reduce<FiltersDisplayCollection>((acc, filterKey) => {
    return ['locations', 'startDate', 'showScheduled'].includes(filterKey)
      ? acc
      : {
          ...acc,
          [filterKey]: {
            ...activeFilters[filterKey],
            label: <FormattedMessage id={`shifts.fields.${filterKey}`} />,
          },
        };
  }, {});

  const shouldShowClearAllButton = Object.keys(filtersDisplayCollection).length > 0;

  const handleClearFilters = () => {
    clearFilters();
  };

  const handleDateRangeChanged = (start: Dayjs, end: Dayjs) => {
    onDateRangeChange && onDateRangeChange(start, end);
  };

  useEffect(() => {
    loadTalents({});
  }, [loadTalents]);

  return (
    <Stack direction={'column'} spacing={1}>
      <Stack
        direction={'row'}
        spacing={2}
        sx={(theme) => ({
          width: '100%',
          height: '40px',
          paddingLeft: theme.spacing(1),
        })}
      >
        <WeekPicker
          loading={loading}
          dateStart={dayjs(startDate)}
          dateEnd={dayjs(endDate)}
          onDateRangeChange={handleDateRangeChanged}
          weekStartDay={weekStartDay}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          style={{
            backgroundColor: 'white',
            color: loading ? theme.palette.text.disabled : theme.palette.text.primary,
          }}
          onClick={() => {
            onToday && onToday();
          }}
        >
          <FormattedMessage id="shifts.switchForTodayButton.label" />
        </Button>
        <IconButton
          aria-label="week back"
          size="small"
          disabled={loading}
          onClick={() => {
            onWeekBack && onWeekBack();
          }}
        >
          <ArrowBackIosNewIcon fontSize="inherit" />
        </IconButton>
        <IconButton
          aria-label="week forward"
          size="small"
          disabled={loading}
          onClick={() => {
            onWeekForward && onWeekForward();
          }}
        >
          <ArrowForwardIosIcon fontSize="inherit" />
        </IconButton>
        <Stack direction={'row'} spacing={2}>
          <FilterMultiSelect
            size="small"
            options={statusFilterOptions}
            inlineLabel
            disabled={loading}
            label={`${intl.formatMessage({ id: 'filters.departments.label' })}:`}
            onApply={(v) => setFilterFromOptions('departments', v, statusFilterOptions)}
            onClear={() => clearFilter('departments')}
            value={getFilter<string[]>('departments')?.value ?? []}
            renderOption={renderCheckboxOption}
          />
          <CalendarShiftsFilterMenu loading={loading} />
          <FormControlLabel
            control={
              <Switch
                defaultChecked={getFilter('showScheduled')?.value == 'yes'}
                onChange={(event, value) => {
                  if (value) {
                    setFilter({
                      key: 'showScheduled',
                      value: value ? 'yes' : '',
                    });
                  } else {
                    clearFilter('showScheduled');
                  }
                }}
              />
            }
            label="Show scheduled"
          />
        </Stack>
        <Box flex={1} />
        <Button
          variant="text"
          disabled={loading}
          onClick={() => {
            onShare && onShare();
          }}
        >
          <FormattedMessage id="shifts.shareShiftButton.label" />
        </Button>
        <Button
          variant="outlined"
          disabled={loading}
          onClick={() => {
            onPublish && onPublish();
          }}
        >
          <FormattedMessage id="shifts.publishShiftButton.label" />
        </Button>
        <MenuSplitButton
          disabled={loading}
          onMainClick={() => {
            onNewShift && onNewShift();
          }}
          label={intl.formatMessage({ id: 'shifts.newShiftButton.label' })}
          menuItems={[
            {
              label: intl.formatMessage({ id: 'shifts.newShiftButton.menu.singleShift.label' }),
              onAction: () => {
                onNewShift && onNewShift();
              },
            },
            {
              label: intl.formatMessage({ id: 'shifts.newShiftButton.menu.newSeries.label' }),
              onAction: () => {
                onNewSeries && onNewSeries();
              },
            },
            {
              label: intl.formatMessage({ id: 'shifts.newShiftButton.menu.newRotation.label' }),
              onAction: () => {
                onNewRotation && onNewRotation();
              },
            },
          ]}
        />
      </Stack>
      {shouldShowClearAllButton && (
        <Stack direction="row" gap={1} flexWrap="wrap">
          <ActiveFilters
            filters={filtersDisplayCollection}
            onDelete={(item) => {
              onListRefresh && onListRefresh();
              clearFilter(item.key);
            }}
          />
          <Fade in={shouldShowClearAllButton} timeout={150}>
            <Button
              variant="passive"
              onClick={() => handleClearFilters()}
              disabled={loading}
              startIcon={<FaIcon name="xmark" />}
              sx={{ ml: 'auto' }}
            >
              <FormattedMessage id="common.clearAllFilters" />
            </Button>
          </Fade>
        </Stack>
      )}
    </Stack>
  );
}

type WeekPickerProps = {
  loading: boolean;
  dateStart: Dayjs;
  dateEnd: Dayjs;
  weekStartDay: number;
  onDateRangeChange: (start: Dayjs, end: Dayjs) => void;
};

function WeekPicker({ loading, dateStart, dateEnd, onDateRangeChange, weekStartDay }: WeekPickerProps) {
  const [hoveredDay, setHoveredDay] = React.useState<Dayjs | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <>
      <Typography
        sx={{
          paddingTop: '8px',
          cursor: 'pointer',
        }}
        onClick={handleClick}
      >
        <FaIcon name="calendar-lines" fontSize={24} />
      </Typography>
      {loading ? (
        <Skeleton>
          <Typography variant="h5" sx={{ paddingTop: '6px', cursor: 'pointer', minWidth: '180px' }} />
        </Skeleton>
      ) : (
        <Typography
          variant="h5"
          sx={{ paddingTop: '6px', cursor: 'pointer', minWidth: '180px', userSelect: 'none' }}
          onClick={handleClick}
        >
          {dateStart.format('MMMM YYYY')}
        </Typography>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          sx={(theme) => ({
            padding: theme.spacing(1),
          })}
        >
          <DateCalendar
            value={dateStart}
            onChange={(newValue) => {
              const startDate = dayjs(newValue).startOf('week');
              const endDate = startDate.add(7, 'days');
              onDateRangeChange(startDate, endDate);
              handleClose();
            }}
            showDaysOutsideCurrentMonth
            disableHighlightToday
            slots={{ day: Day }}
            slotProps={{
              day: (ownerState) => ({
                selectedDay: dateStart,
                hoveredDay,
                weekStartDay,
                onPointerEnter: () => setHoveredDay(ownerState.day),
                onPointerLeave: () => setHoveredDay(null),
              }),
            }}
          />
        </Box>
      </Popover>
    </>
  );
}

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean;
  isHovered: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isHovered',
})<CustomPickerDayProps>(({ theme, isSelected, isHovered, day }) => ({
  borderRadius: 0,
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 0,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.main,
    },
  }),
  ...(isHovered && {
    backgroundColor: theme.palette.primary.light,
    borderRadius: 0,
    '&:hover, &:focus': {
      backgroundColor: theme.palette.primary.light,
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.primary.dark,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.dark,
      },
    }),
  }),
  ':first-of-type': {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  ':last-of-type': {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
  },
})) as React.ComponentType<CustomPickerDayProps>;

const isInSameWeek = (dayA: Dayjs, dayB: Dayjs | null | undefined, weekStartDay: number) => {
  if (dayB == null) {
    return false;
  }
  return dayA.add(weekStartDay < 0 ? 1 : 0, 'day').isSame(dayB, 'week');
};

function Day(
  props: PickersDayProps<Dayjs> & {
    selectedDay?: Dayjs | null;
    hoveredDay?: Dayjs | null;
    weekStartDay?: number;
  },
) {
  const { day, selectedDay, hoveredDay, weekStartDay, ...other } = props;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ px: 2.5 }}
      disableMargin
      selected={false}
      isSelected={isInSameWeek(day, selectedDay, weekStartDay || 0)}
      isHovered={isInSameWeek(day, hoveredDay, weekStartDay || 0)}
    />
  );
}
